import { createContext, useContext, useState } from "react";
import { cn } from "../../../utils/styles.utils";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";

const TableSearchContext = createContext({
  enabled: false,
  query: "",
  mode: "client",
});

const TableSectionContext = createContext({
  inBody: false,
});

const TableLayoutContext = createContext({
  stickyHeader: false,
  stickyHeaderOffset: 0,
});

function normalizeSearchValue(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

function extractNodeText(node) {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node))
    return node.map((item) => extractNodeText(item)).join(" ");
  if (typeof node === "object" && node.props)
    return extractNodeText(node.props.children);
  return "";
}

function createVariants({ base = "", variants = {}, default: defaults = {} }) {
  return function buildVariantClasses(selection = {}) {
    const resolvedSelection = { ...defaults, ...selection };
    const classes = [base];

    for (const [variantName, variantMap] of Object.entries(variants)) {
      const selectedKey = resolvedSelection[variantName];
      if (selectedKey && variantMap[selectedKey]) {
        classes.push(variantMap[selectedKey]);
      }
    }

    return classes.filter(Boolean).join(" ");
  };
}

/** Variants for the Table component. */
export const tableVariants = createVariants({
  base: "w-full border-separate border-spacing-0 rounded-2xl border border-slate-200 bg-slate-50 text-slate-800 shadow-[0_1px_2px_rgba(15,23,42,0.06)]",
  variants: {
    density: {
      compact: "text-sm [&_th]:px-4 [&_th]:py-2.5 [&_td]:px-4 [&_td]:py-2.5",
      comfortable: "text-base [&_th]:px-5 [&_th]:py-3 [&_td]:px-5 [&_td]:py-3",
      spacious: "text-lg [&_th]:px-6 [&_th]:py-3.5 [&_td]:px-6 [&_td]:py-3.5",
    },
  },
  default: {
    density: "comfortable",
  },
});

/**
 * @description A tabular data structure that organizes information into rows and columns.
 */
export function Table({
  density = "comfortable",
  searchable = true,
  searchMode = "client",
  searchPlaceholder = "Search table...",
  searchValue,
  onSearchChange,
  stickyHeader = false,
  stickyHeaderOffset = 0,
  className,

  ...props
}) {
  const isControlled = searchValue !== undefined;
  const [internalSearchValue, setInternalSearchValue] = useState("");
  const resolvedStickyHeaderOffset =
    stickyHeaderOffset || (searchable ? 52 : 0);

  const query = normalizeSearchValue(
    isControlled ? searchValue : internalSearchValue,
  );

  const handleSearchChange = (event) => {
    const nextValue = event.target.value;
    if (!isControlled) setInternalSearchValue(nextValue);
    if (onSearchChange) onSearchChange(nextValue);
  };

  return (
    <TableLayoutContext.Provider
      value={{
        stickyHeader,
        stickyHeaderOffset: resolvedStickyHeaderOffset,
      }}
    >
      <TableSearchContext.Provider
        value={{ enabled: searchable, query, mode: searchMode }}
      >
        <>
          {searchable ? (
            <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm">
              <input
                type="text"
                value={isControlled ? (searchValue ?? "") : internalSearchValue}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                aria-label="Search table"
                className="mb-3 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition-colors duration-150 placeholder:text-slate-400 focus:border-sky-400"
              />
            </div>
          ) : null}

          <table
            className={cn(tableVariants({ density }), className)}
            data-slot="table"
            aria-label="table"
            {...props}
          />
        </>
      </TableSearchContext.Provider>
    </TableLayoutContext.Provider>
  );
}

/**
 * @description Semantic table header wrapper that keeps sticky state support.
 */
export function TableHeader({ className, ...props }) {
  const { stickyHeader, stickyHeaderOffset } = useContext(TableLayoutContext);

  return (
    <thead
      className={cn("text-slate-900", className)}
      style={
        stickyHeader
          ? {
              "--table-sticky-header-offset": `${stickyHeaderOffset}px`,
            }
          : undefined
      }
      data-slot="table-header"
      {...props}
    />
  );
}

/**
 * @description Semantic table body wrapper.
 */
export function TableBody({ ...props }) {
  return (
    <TableSectionContext.Provider value={{ inBody: true }}>
      <tbody data-slot="table-body" {...props} />
    </TableSectionContext.Provider>
  );
}

/**
 * @description Semantic table footer wrapper.
 */
export function TableFooter({ className, ...props }) {
  return (
    <tfoot
      className={cn("bg-slate-100/70 text-slate-900 font-semibold", className)}
      data-slot="table-footer"
      {...props}
    />
  );
}

/**
 * @description Table row that supports hover, selection, and pinned styling.
 */
export function TableRow({ className, ...props }) {
  const { enabled, query, mode } = useContext(TableSearchContext);
  const { inBody } = useContext(TableSectionContext);

  if (enabled && mode === "client" && inBody && query) {
    const rowText = normalizeSearchValue(extractNodeText(props.children));
    if (!rowText.includes(query)) return null;
  }

  return (
    <tr
      className={cn(
        "transition-colors duration-150 hover:bg-sky-50/80 focus-within:bg-sky-50/80 [&:last-child>td]:border-b-0",
        className,
      )}
      data-slot="table-row"
      aria-selected={props["aria-selected"]}
      {...props}
    />
  );
}

/** Variants for the Table cell component. */
export const tableCellVariants = createVariants({
  base: "border-b border-slate-300/70 align-middle text-left first:pl-6 last:pr-6",
  variants: {
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
      justify: "text-justify",
      char: "text-left",
    },
  },
  default: {
    align: "left",
  },
});

/**
 * @description Table header cell that wires alignment, sort affordances, and column-resize anchors.
 */
export function TableHead({
  className,
  align = "left",
  children,
  sortable,
  showSortIcon = true,
  ...props
}) {
  const ariaSort = props["aria-sort"] ?? "none";
  const isSortable = sortable ?? typeof props.onClick === "function";
  const { stickyHeader } = useContext(TableLayoutContext);
  const SortIcon =
    ariaSort === "ascending"
      ? FaSortUp
      : ariaSort === "descending"
        ? FaSortDown
        : FaSort;

  return (
    <th
      className={cn(
        "font-semibold text-slate-900",
        isSortable && "cursor-pointer select-none",
        tableCellVariants({ align }),
        className,
      )}
      data-slot="table-head"
      data-align={align}
      {...props}
      aria-sort={ariaSort}
    >
      <span className="inline-flex items-center gap-2 [&_.ms-1]:hidden">
        <span>{children}</span>
        {isSortable && showSortIcon ? (
          <SortIcon
            className={cn(
              "h-3.5 w-3.5 shrink-0",
              ariaSort === "none" ? "text-slate-400" : "text-sky-600",
            )}
            aria-hidden="true"
          />
        ) : null}
      </span>
    </th>
  );
}

/**
 * @description Table cell capable of hosting arbitrary components, including form controls.
 */
export function TableCell({ className, align = "left", ...props }) {
  return (
    <td
      className={cn("text-slate-700", tableCellVariants({ align }), className)}
      data-slot="table-cell"
      data-align={align}
      {...props}
    />
  );
}
